/**
 * DataField — 詳情頁通用「標籤 + 值」對子（v3 "Quiet Refinement"）
 * ──────────────────────────────────────────────
 * 升級亮點：
 *   - 標籤：mono uppercase tracked，stone-400 弱化（更輕）
 *   - 值：regular 字重，石黑色主文字
 *   - 空值：渲染 em-dash「—」（不是 N/A 或空白）
 *   - mono 模式：IBM Plex Mono + tabular-nums（編號/ID 場景）
 *   - span 跨度支持（用於 DataFieldGrid 多列場景）
 *
 * 用途場景：
 *   - 詳情頁屬性網格（基本資訊 / 聯絡方式 / 案件 metadata）
 *   - Drawer 中的對象資訊
 *   - 報告 / 證書 / 決定書的元數據
 */
import type { ReactNode } from 'react';
import { color, font, leading, space, text, tracking, weight } from '@/styles/tokens';

export interface DataFieldProps {
  /** 標籤 */
  label: ReactNode;
  /** 值（傳 null/undefined 自動渲染 em-dash） */
  value: ReactNode;
  /** 圖標（顯示在標籤前） */
  icon?: ReactNode;
  /** 是否使用 mono 字體（編號/ID 場景，自動開 tabular-nums） */
  mono?: boolean;
  /** 排列方向：horizontal=label/value 一行 | vertical=label 在上 value 在下 */
  direction?: 'horizontal' | 'vertical';
  /** 跨度（網格場景，配合 DataFieldGrid） */
  span?: 1 | 2 | 3 | 4;
}

export default function DataField({
  label,
  value,
  icon,
  mono,
  direction = 'vertical',
  span,
}: DataFieldProps) {
  const isHorizontal = direction === 'horizontal';

  return (
    <div
      style={{
        display: isHorizontal ? 'flex' : 'block',
        alignItems: isHorizontal ? 'baseline' : undefined,
        gap: isHorizontal ? space[3] : undefined,
        gridColumn: span ? `span ${span}` : undefined,
        minWidth: 0,
      }}
    >
      {/* 標籤：mono uppercase，stone-400 極弱化 */}
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 5,
          fontFamily: font.mono,
          fontSize: text.xs,
          color: color.text.muted,
          textTransform: 'uppercase',
          letterSpacing: tracking.widest,
          fontWeight: weight.medium,
          marginBottom: isHorizontal ? 0 : 4,
          flexShrink: 0,
          lineHeight: 1.4,
        }}
      >
        {icon && (
          <span style={{ display: 'inline-flex', flexShrink: 0, color: color.text.muted }}>
            {icon}
          </span>
        )}
        {label}
      </div>

      {/* 值 */}
      <div
        style={{
          fontSize: text.md,
          color: color.text.primary,
          fontFamily: mono ? font.mono : font.body,
          fontWeight: weight.regular,
          fontVariantNumeric: mono ? 'tabular-nums' : undefined,
          lineHeight: leading.snug,
          minWidth: 0,
          wordBreak: 'break-word',
        }}
      >
        {/* 空值渲染 em-dash */}
        {value == null || value === '' ? (
          <span style={{ color: color.text.disabled, fontFamily: font.body }}>—</span>
        ) : (
          value
        )}
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────
 * DataFieldGrid — 響應式網格，自動排列 DataField
 * ──────────────────────────────────────────────── */

export interface DataFieldGridProps {
  /** 列數（響應式自動降階） */
  columns?: 1 | 2 | 3 | 4;
  /** 行間距，預設 space[5]（20px） */
  rowGap?: number;
  /** 列間距，預設 space[6]（24px） */
  columnGap?: number;
  children: ReactNode;
}

export function DataFieldGrid({
  columns = 2,
  rowGap = space[5],
  columnGap = space[6],
  children,
}: DataFieldGridProps) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
        rowGap,
        columnGap,
      }}
    >
      {children}
    </div>
  );
}
